import { useEffect, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import { useAppSelector } from 'src/hooks';
import TableComponent from 'src/components/collapsible-table/table';
import { getTableColumnData } from 'src/components/collapsible-table/table-column-prep';
import _ from 'lodash';
import { useReactTable } from '@tanstack/react-table';
import {
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  RowSelectionState
} from '@tanstack/table-core';
import { TableComponent as TableType } from '../pricing-simulator/types/generic-table.model';
import TableSkelatonContainer from '../pricing-simulator/components/simulation-table-container/table-skeleton';
import { getFilteredRows } from './scenario-manager-util';

interface IScenarioManagerTableComponentProps {
  onRowSelectionChange: (
    selectedRows: { [key: string]: string | number }[]
  ) => void;
}

/* 
  Table for scenario manager
*/
export default function ScenarioManagerTableComponent({
  onRowSelectionChange
}: IScenarioManagerTableComponentProps) {
  const {
    scenarioManagerTableData,
    fetchingTableData,
    inputCols,
    isDeletingScenario,
    isFinalizingScenario,
    isArchivingScenario
  } = useAppSelector((state) => state.scenarioManager);

  const { activeCheckboxValues, searchInputVal } = useAppSelector(
    (state) => state.scenarioManagerTopBar
  );

  const [columns, setColumns] = useState([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const [rows, setRows] = useState(
    getFilteredRows(
      scenarioManagerTableData.row,
      searchInputVal,
      activeCheckboxValues
    ).sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    )
  );
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const tableRef = useRef<HTMLTableElement>();

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      expanded,
      rowSelection
    },
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    getSubRows: (row: any) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel()
  });

  useEffect(() => {
    setRows(
      getFilteredRows(
        scenarioManagerTableData.row,
        searchInputVal,
        activeCheckboxValues
      ).sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      )
    );
    table.toggleAllRowsSelected(false);
  }, [
    isDeletingScenario,
    isFinalizingScenario,
    isArchivingScenario,
    scenarioManagerTableData.row
  ]);

  useEffect(() => {
    setColumns([...getTableColumnData(inputCols, TableType.SCENARIOMANAGER, [])]);
  }, [inputCols]);

  useEffect(() => {
    setRows(
      getFilteredRows(
        scenarioManagerTableData.row,
        searchInputVal,
        activeCheckboxValues
      ).sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      )
    );
  }, [activeCheckboxValues, searchInputVal]);

  useEffect(() => {
    onRowSelectionChange(
      table.getSelectedRowModel().flatRows.map((row) => row.original)
    );
  }, [rowSelection]);

  return (
    <Grid container>
      {(fetchingTableData || !columns.length) && (
        <Grid item xs={12}>
          <TableSkelatonContainer rowLength={10} headerLength={6} />
        </Grid>
      )}
      {!fetchingTableData && (
        <Grid item xs={12}>
          <TableComponent
            setHeightExplicitly
            table={table}
            extClassName='scenario-manager-table'
            tableRef={tableRef}
          />
        </Grid>
      )}
    </Grid>
  );
}
